import { datetimeLocalToInstant, decodeJobStatus, instantToDatetimeLocal } from "@/app/appointments/availability/lib/editorModel"
import type { SlotPlanJobStatus } from "@/app/appointments/availability/lib/editorModel"
import { SITE_TIME_ZONE, longDateLabel, minutesToLabel, zonedDateAndMinutes } from "@/app/booking-calendar/lib/siteTime"
import type { StaffMoveRequest, StaffMoveSiteResult } from "../staffMovesApi"

/**
 * The Move staff page's form (AB#714), as its inputs hold it. `effectiveAt` is the native
 * `datetime-local` value on the site's clock — "" for now, which is the default.
 */
export interface MoveForm {
  fromTeamSlug: string
  toTeamSlug: string
  calUserId: number | null
  effectiveAt: string
  /** The host's address, from the organization's users; sent so the server need not search for it. */
  upn: string | null
}

export const EMPTY_FORM: MoveForm = { fromTeamSlug: "", toTeamSlug: "", calUserId: null, effectiveAt: "", upn: null }

/** Everything that keeps the form from being previewed, one sentence each. */
export function formProblems(form: MoveForm, timeZone: string = SITE_TIME_ZONE, now: Date = new Date()): string[] {
  const problems: string[] = []

  if (!form.fromTeamSlug) problems.push("Choose the site they are leaving.")
  if (!form.toTeamSlug) problems.push("Choose the site they are moving to.")
  if (form.fromTeamSlug && form.fromTeamSlug === form.toTeamSlug) problems.push("Choose a different site to move them to.")
  if (!form.calUserId) problems.push("Choose who is moving.")

  if (form.effectiveAt.trim() !== "") {
    const instant = datetimeLocalToInstant(form.effectiveAt, timeZone)
    if (!instant) {
      problems.push("The effective date-time is not a date and time.")
    } else if (new Date(instant).getTime() < now.getTime()) {
      problems.push("The effective date-time has passed. Leave it blank to move them now.")
    }
  }

  return problems
}

/** The request the form stands for; null while it has problems. */
export function buildMoveRequest(form: MoveForm, timeZone: string = SITE_TIME_ZONE, now: Date = new Date()): StaffMoveRequest | null {
  if (formProblems(form, timeZone, now).length > 0 || !form.calUserId) return null

  return {
    fromTeamSlug: form.fromTeamSlug,
    toTeamSlug: form.toTeamSlug,
    calUserId: form.calUserId,
    effectiveAt: form.effectiveAt.trim() === "" ? null : datetimeLocalToInstant(form.effectiveAt, timeZone),
    upn: form.upn,
  }
}

/** Whether two requests ask for the same move, so a preview still answers the form. */
export function sameMove(a: StaffMoveRequest | null, b: StaffMoveRequest | null): boolean {
  if (!a || !b) return false
  return a.fromTeamSlug === b.fromTeamSlug
    && a.toTeamSlug === b.toTeamSlug
    && a.calUserId === b.calUserId
    && (a.effectiveAt ?? null) === (b.effectiveAt ?? null)
}

/** "Wednesday, Oct 14 at 1:00 PM" on the site's clock, or "now". */
export function effectiveLabel(instant: string | null | undefined, immediate: boolean, timeZone: string = SITE_TIME_ZONE): string {
  if (immediate || !instant) return "now"
  const { date, minutes } = zonedDateAndMinutes(instant, timeZone)
  return `${longDateLabel(date)} at ${minutesToLabel(minutes)}`
}

/** Today's date and the time, as the native input holds them: what "now" would read as if typed. */
export function nowAsDatetimeLocal(timeZone: string = SITE_TIME_ZONE, now: Date = new Date()): string {
  return instantToDatetimeLocal(now.toISOString(), timeZone)
}

/** Where to set the host's hours at the new site: the availability editor, opened on them. */
export function availabilityLink(teamSlug: string, calUserId: number): string {
  const search = new URLSearchParams({ site: teamSlug, view: "host", host: String(calUserId) })
  return `/appointments/availability?${search.toString()}`
}

/** One site's outcome in words. */
export function siteStatusLabel(result: Pick<StaffMoveSiteResult, "status" | "error" | "role">): string {
  switch (result.status) {
    case "added":
      return result.error
        ? "Added to the team, but their host setup did not finish"
        : "Added to the team — unavailable until their hours are set"
    case "already-member":
      return "Already on the team — their hours there were left as they were"
    case "ended":
      return "Their availability here has ended"
    case "ending":
      return "Their availability here ends at the effective time"
    default:
      return result.role === "from"
        ? "Their availability here could not be ended"
        : "They could not be added to the team"
  }
}

/** Whether a site's outcome needs somebody to look at it. */
export function siteFailed(result: Pick<StaffMoveSiteResult, "status" | "error">): boolean {
  return result.status === "failed" || !!result.error
}

/** The old site's job status, decoded; null for the new site or a job not run. */
export function siteJobStatus(result: Pick<StaffMoveSiteResult, "jobStatus">): SlotPlanJobStatus | null {
  return decodeJobStatus(result.jobStatus ?? null)
}

/** What a refused preview or move says: a 400 or 404 lists every problem; a 409 is the plan's version check. */
export function describeMoveError(error: unknown): string[] {
  const { status, data } = (error ?? {}) as { status?: number | string; data?: unknown }
  const errors = (data as { errors?: unknown } | null | undefined)?.errors

  if ((status === 400 || status === 404) && Array.isArray(errors) && errors.length > 0) {
    return errors.map(String)
  }
  if (status === 409) {
    return [typeof data === "string" && data.trim() !== ""
      ? data
      : "Their plan at the old site changed while the move was being made. Nothing was changed; try again."]
  }
  if (status === 403) return ["You do not have permission to move staff between sites."]
  if (status === 404) return ["That site or host could not be found. Pick them again."]
  return ["The move could not be made. Try again in a moment."]
}
